type Rgb = { r: number; g: number; b: number };

function hexToRgb(hex: string): Rgb | null {
  const cleaned = hex.trim().replace(/^#/, "");

  // Expand shorthand (#abc -> #aabbcc).
  const full =
    cleaned.length === 3
      ? cleaned
          .split("")
          .map((c) => c + c)
          .join("")
      : cleaned;

  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;

  const num = parseInt(full, 16);
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
}

// Relative luminance per WCAG 2.x (sRGB).
function getLuminance({ r, g, b }: Rgb): number {
  const convert = (value: number) => {
    const s = value / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * convert(r) + 0.7152 * convert(g) + 0.0722 * convert(b);
}

/**
 * Returns the foreground text color (#FFFFFF or #000000) with the highest WCAG
 * contrast against `backgroundColor` — so text on a brand-colored surface stays
 * legible whatever color the merchant picks. Falls back to black when the input
 * isn't a parseable hex color.
 */
export function getForegroundColor(
  backgroundColor: string
): "#FFFFFF" | "#000000" {
  const rgb = hexToRgb(backgroundColor);
  if (!rgb) return "#000000";

  const luminance = getLuminance(rgb);
  // Contrast ratio of white (L=1) vs. of black (L=0) against the background.
  const whiteContrast = 1.05 / (luminance + 0.05);
  const blackContrast = (luminance + 0.05) / 0.05;

  return whiteContrast > blackContrast ? "#FFFFFF" : "#000000";
}
